import type { Commands } from "../../input/commands";
import type { InputState } from "../../input/state";
import { $roomAction, $roomPhase } from "../../room/phase";
import { $mailFailed, $mailSummary } from "../../state/mail";
import { $lookMarker } from "../../state/markers";
import { $lookAt, $placeMode, $presence } from "../../state/stores";
import { $activeTool, $confirmReady } from "../../state/tools";
import { badgeText } from "../mail";
import { buildMode, contextAction, type IconId, joinLabel, topIcons } from "./context";
import { pressable } from "./press";
import { canFullscreen, goFullscreen, isAndroid } from "./fullscreen";

const make = (tag: string, cls: string, text = ""): HTMLElement => {
  const el = document.createElement(tag);
  el.className = cls;
  el.textContent = text;
  return el;
};

// a figure mid-jump with speed lines under it: drawn, so it looks the same on every phone's fonts
const JUMP_ICON = '<svg viewBox="0 0 24 24" width="36" height="36" aria-hidden="true" fill="none" stroke="currentColor" ' +
  'stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="13" cy="3.2" r="2.2" fill="currentColor" stroke="none"/>' +
  '<path d="M12.4 7 11 12.5M8 5.2l4.3 2.9 4.4-3.2M11 12.5l4 2-1 4M11 12.5l-3 3-3-1"/>' +
  '<path d="M7.5 18.5v3.5M10.5 16.5v6M17 17.5v3.5" stroke-width="1.5" opacity=".6"/></svg>';

/** The touch buttons over the layer (spec 2p §2.2, §2.4). Every one calls a command; none has rules of its own. */
export function mountTouchButtons(root: HTMLElement, cmd: Commands, input: InputState): void {
  const ui = make("div", "touch-ui");
  const menu = make("button", "touch-menu", "≡");
  menu.setAttribute("aria-label", "選單");
  pressable(menu, { onPress: () => cmd.back() });
  const top = make("div", "touch-top");
  const fs = make("button", "touch-fs", "⛶");
  fs.setAttribute("aria-label", "全螢幕");
  pressable(fs, { onPress: () => void goFullscreen() });
  const renderFs = () => {
    fs.hidden = !isAndroid() || !canFullscreen() || !!document.fullscreenElement;
    document.body.classList.toggle("has-fs", !fs.hidden); // the coordinates move over only while ⛶ shows
  };
  document.addEventListener("fullscreenchange", renderFs);

  const actions = make("div", "touch-actions");
  const jump = make("button", "touch-jump");
  jump.setAttribute("aria-label", "跳");
  jump.innerHTML = JUMP_ICON;
  pressable(jump, { onDown: () => input.setJump(true), onUp: () => input.setJump(false) });
  const ctx = make("button", "touch-context");
  pressable(ctx, { onPress: () => cmd.interact() });
  const join = make("button", "touch-join");
  pressable(join, { onPress: () => cmd.toggleJoin() });
  actions.append(join, ctx, jump);

  const build = make("div", "touch-build");
  const rotL = make("button", "touch-rot-left", "⟲");
  const rotR = make("button", "touch-rot-right", "⟳");
  pressable(rotL, { onDown: () => cmd.rotate(-15), repeatMs: 150 });
  pressable(rotR, { onDown: () => cmd.rotate(15), repeatMs: 150 });
  const cancel = make("button", "touch-cancel", "✕ 取消");
  pressable(cancel, { onPress: () => cmd.cancel() });
  const ok = make("button", "touch-ok");
  pressable(ok, { onPress: () => cmd.confirm() });
  build.append(rotL, rotR, cancel, ok);

  ui.append(menu, fs, top, actions, build);
  root.appendChild(ui);

  root.querySelectorAll<HTMLElement>("#hotbar .slot").forEach((slot, i) => pressable(slot, { onPress: () => cmd.selectSlot(i) }));

  const run: Record<IconId, () => void> = {
    chat: () => cmd.openChat(),
    mail: () => cmd.openPanel("mail"),
    inventory: () => cmd.openPanel("inventory"),
    plot: () => cmd.openPanel("plot"),
    room: () => cmd.toggleRoom(),
  };
  let iconsFor: boolean | null = null;
  let mailDot: HTMLElement | null = null;
  const renderTop = (inRoom: boolean) => {
    if (iconsFor === inRoom) return;
    iconsFor = inRoom;
    top.replaceChildren(...topIcons(inRoom).map((icon) => {
      const b = make("button", "touch-icon", icon.glyph);
      b.dataset.icon = icon.id;
      b.setAttribute("aria-label", icon.label);
      if (icon.id === "mail") { mailDot = make("span", "touch-dot"); b.appendChild(mailDot); }
      pressable(b, { onPress: run[icon.id] });
      return b;
    }));
    renderMail();
  };
  const renderMail = () => {
    const text = badgeText($mailSummary.get(), $mailFailed.get());
    if (!mailDot) return;
    mailDot.textContent = text ?? "";
    mailDot.hidden = !text;
  };

  const render = () => {
    const inRoom = $roomPhase.get() !== "lobby";
    renderTop(inRoom);
    const mode = buildMode(!!$placeMode.get(), $activeTool.get());
    actions.hidden = mode !== null;
    build.hidden = mode === null;
    rotL.hidden = rotR.hidden = mode !== "place";
    ok.textContent = mode === "marker" ? "✓ 標記這裡" : "✓ 放置";
    ok.classList.toggle("dim", !$confirmReady.get());
    const c = contextAction({ inRoom, roomAction: $roomAction.get(), lookMarker: !!$lookMarker.get(), removable: !!$lookAt.get()?.removable, building: mode !== null });
    ctx.hidden = !c;
    ctx.textContent = c?.label ?? "";
    ctx.classList.toggle("danger", !!c?.danger);
    const j = inRoom ? null : joinLabel($presence.get());
    join.hidden = !j;
    join.textContent = j ?? "";
  };
  for (const s of [$roomPhase, $placeMode, $activeTool, $confirmReady, $roomAction, $lookAt, $lookMarker, $presence]) s.subscribe(render);
  $mailSummary.subscribe(renderMail);
  $mailFailed.subscribe(renderMail);
  renderFs();
}
